import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

const BUILD_VERSION_META_NAME = 'qed-build-version';
const VERSION_CHECK_INTERVAL_MS = 30000;
const VERSION_ENDPOINT = '/version.json';

type ReloadGuard = () => boolean | Promise<boolean>;

interface VersionWatcherContextValue {
  registerReloadGuard: (guard: ReloadGuard | null) => void;
}

const VersionWatcherContext = createContext<VersionWatcherContextValue | null>(null);

function getCurrentBuildVersion(): string | null {
  const metaTag = document.querySelector<HTMLMetaElement>(
    `meta[name="${BUILD_VERSION_META_NAME}"]`
  );
  const version = metaTag?.content?.trim();
  return version || null;
}

async function fetchLatestBuildVersion(): Promise<string | null> {
  const response = await fetch(VERSION_ENDPOINT, {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
    },
  });

  if (!response.ok) {
    return null;
  }

  const payload = await response.json();
  return typeof payload?.version === 'string' && payload.version.trim()
    ? payload.version.trim()
    : null;
}

interface VersionWatcherProps {
  children: ReactNode;
}

export function VersionWatcher({ children }: VersionWatcherProps) {
  const location = useLocation();
  const currentBuildVersionRef = useRef<string | null>(getCurrentBuildVersion());
  const latestBuildVersionRef = useRef<string | null>(null);
  const reloadGuardRef = useRef<ReloadGuard | null>(null);
  const checkInFlightRef = useRef(false);
  const [pendingReload, setPendingReload] = useState(false);

  const isEditorRoute = location.pathname.startsWith('/project/');

  const reloadPage = useCallback(() => {
    window.location.reload();
  }, []);

  const attemptReload = useCallback(async () => {
    const currentBuildVersion = currentBuildVersionRef.current;
    const latestBuildVersion = latestBuildVersionRef.current;

    if (!currentBuildVersion || !latestBuildVersion || currentBuildVersion === latestBuildVersion) {
      return;
    }

    if (!isEditorRoute) {
      reloadPage();
      return;
    }

    setPendingReload(true);

    const reloadGuard = reloadGuardRef.current;
    if (!reloadGuard) {
      return;
    }

    try {
      const safeToReload = await reloadGuard();
      if (safeToReload) {
        reloadPage();
      }
    } catch (error) {
      console.error('Failed while preparing the editor for a version reload:', error);
    }
  }, [isEditorRoute, reloadPage]);

  const checkForNewVersion = useCallback(async () => {
    if (checkInFlightRef.current || !currentBuildVersionRef.current) {
      return;
    }

    checkInFlightRef.current = true;
    try {
      const latestBuildVersion = await fetchLatestBuildVersion();
      if (!latestBuildVersion || latestBuildVersion === currentBuildVersionRef.current) {
        return;
      }

      latestBuildVersionRef.current = latestBuildVersion;
      await attemptReload();
    } catch (error) {
      console.error('Failed to check for a newer frontend version:', error);
    } finally {
      checkInFlightRef.current = false;
    }
  }, [attemptReload]);

  useEffect(() => {
    if (!currentBuildVersionRef.current) {
      return undefined;
    }

    void checkForNewVersion();
    const intervalId = window.setInterval(() => {
      void checkForNewVersion();
    }, VERSION_CHECK_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [checkForNewVersion]);

  useEffect(() => {
    if (!currentBuildVersionRef.current) {
      return undefined;
    }

    const handleFocus = () => {
      if (pendingReload) {
        void attemptReload();
        return;
      }

      void checkForNewVersion();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      if (pendingReload) {
        void attemptReload();
        return;
      }

      void checkForNewVersion();
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [attemptReload, checkForNewVersion, pendingReload]);

  useEffect(() => {
    if (pendingReload && !isEditorRoute) {
      reloadPage();
    }
  }, [isEditorRoute, pendingReload, reloadPage]);

  const contextValue = useMemo<VersionWatcherContextValue>(() => ({
    registerReloadGuard: (guard) => {
      reloadGuardRef.current = guard;
    },
  }), []);

  return (
    <VersionWatcherContext.Provider value={contextValue}>
      {children}
    </VersionWatcherContext.Provider>
  );
}

export function useVersionReloadGuard(guard: ReloadGuard | null) {
  const context = useContext(VersionWatcherContext);

  useEffect(() => {
    context?.registerReloadGuard(guard);
    return () => context?.registerReloadGuard(null);
  }, [context, guard]);
}
